import get from 'lodash/get';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { push } from 'react-router-redux';
import { call, put, select } from 'redux-saga/effects';

import * as PodcastActions from '../../actions';
import * as PodcastConstants from '../../constants';
import * as D from '../../deps';
import * as AesopServices from '../../services';
import * as PodcastUtils from '../../utils';

export function* getDetails({ data = {} } = {}) {
  const listenerId = yield select(D.Utils.Listener.getCurrentListenerIdFromState);
  yield call(AesopServices.getDetails, {
    listenerId,
    pandoraId: data.pandoraId,
    sortingOrder: data.sortingOrder,
  });
}

export function* getAllThumbedEpisodesByPodcastProgram({ data = {} } = {}) {
  const listenerId = yield select(D.Utils.Listener.getCurrentListenerIdFromState);
  yield call(AesopServices.getAllThumbedEpisodesByPodcastProgram, {
    listenerId,
    pandoraId: data.pandoraId,
    isPositive: data.isPositive,
  });
}

export function* getAllEpisodesByPodcastProgram({ data = {} } = {}) {
  const listenerId = yield select(D.Utils.Listener.getCurrentListenerIdFromState);
  yield call(AesopServices.getAllEpisodesByPodcastProgram, {
    listenerId,
    pandoraId: data.pandoraId,
    sortingOrder: data.sortingOrder,
  });
}

export function* removeEpisodesFeedback({ data = {} } = {}) {
  const listenerId = yield select(D.Utils.Listener.getCurrentListenerIdFromState);
  yield call(AesopServices.removeEpisodesFeedback, {
    listenerId,
    episodeIds: data.episodeIds || [],
    pandoraId: data.pandoraId,
  });
}

export function* annotateAesopObjects({
  payload: { pandoraIds, withProgress, dedupeAnnotationsRequest, ignoreAnnotations },
}) {
  const listenerId = yield select(D.Utils.Listener.getCurrentListenerIdFromState);
  const idsNeedingAnnotations = dedupeAnnotationsRequest
    ? yield select(D.Utils.Entity.getPandoraIdsWithoutAnnotations, pandoraIds)
    : pandoraIds;

  if (idsNeedingAnnotations.length > 0) {
    yield call(AesopServices.annotateObjects, {
      listenerId,
      pandoraIds: idsNeedingAnnotations,
      ignoreAnnotations,
      withProgress,
    });
  }
}

export function* handlePodcastEpisodeProgress({ pandoraId, elapsedTime, isFinished }) {
  const annotations = yield select(D.getAnnotationsById, pandoraId);

  if (annotations) {
    const episodeDuration = annotations.get('duration');
    const hasCompleted = episodeDuration - elapsedTime <= 0;
    yield put(
      PodcastActions.updatePodcastEpisodeProgress({
        pandoraId,
        elapsedTime,
        isFinished: hasCompleted || isFinished,
      }),
    );
  }
}

export function* handleUserProgramStatus({ episodeId }) {
  const episode = yield select(D.getAnnotationsById, episodeId);

  if (episode) {
    const programId = episode && episode.get('podcastId');
    const activeProgramId = yield select(PodcastUtils.getActiveBackstageProgramId);
    const program = yield select(PodcastUtils.getPodcastProgramDetailsById, programId);
    const currentContinueListeningEpisode = program && program.get('continueListeningEpisode');

    if (programId === activeProgramId && episodeId !== currentContinueListeningEpisode) {
      yield put(PodcastActions.requestGetDetails({ pandoraId: programId }));
    }
  }
}

export function* onAudioPlayerStarted({ payload: { token: trackId } }) {
  if (D.Utils.SourceType.isPodcastEpisodePandoraId(trackId)) {
    const elapsedTime = yield select(D.getInDomain('playback.utils.getCurrentTrackTime', 0));
    yield call(handlePodcastEpisodeProgress, {
      pandoraId: trackId,
      elapsedTime,
      isFinished: false,
    });

    yield call(handleUserProgramStatus, {
      episodeId: trackId,
    });
  }
}

export function* onAudioPlayerPaused({ payload: { token: trackId } }) {
  if (D.Utils.SourceType.isPodcastEpisodePandoraId(trackId)) {
    const elapsedTime = yield select(D.getInDomain('playback.utils.getCurrentTrackTime', 0));
    yield call(handlePodcastEpisodeProgress, {
      pandoraId: trackId,
      elapsedTime,
      isFinished: false,
    });
  }
}

export function* onAudioPlayerEnded({ payload }) {
  const trackId = yield select(
    D.getInDomain('playback.utils.getCurrentTrackTokenFromActivePlaylist', null),
  );

  if (D.Utils.SourceType.isPodcastEpisodePandoraId(trackId)) {
    yield call(handlePodcastEpisodeProgress, {
      pandoraId: trackId,
      elapsedTime: payload.currentTime,
      isFinished: true,
    });
  }
}

export function* onAudioPlayerScrubbed({ payload }) {
  const trackId = yield select(
    D.getInDomain('playback.utils.getCurrentTrackTokenFromActivePlaylist', null),
  );
  const isPlaying = yield select(D.getInDomain('playback.utils.isAudioPlaying', false));

  if (!isPlaying && D.Utils.SourceType.isPodcastEpisodePandoraId(trackId)) {
    yield call(handlePodcastEpisodeProgress, {
      pandoraId: trackId,
      elapsedTime: payload.value,
      isFinished: false,
    });
  }
}

export function* getItemFeedbackIds({ payload: { trackToken: episodeId } }) {
  const episode = yield select(D.getAnnotationsById, episodeId);
  const programId = episode && episode.get('podcastId');

  return { programId, episodeId };
}

export function* updateThumbsList({
  episodeId,
  isAddingThumbUp,
  isRemovingThumbUp,
  isAddingThumbDown,
  isRemovingThumbDown,
}) {
  const currentPath = yield select(D.Utils.Route.getCurrentPathnameFromState);
  const isThumbedUpEpisodePath = D.Utils.Route.isBackstagePodcastThumbedUpEpisodesPath(currentPath);
  const isThumbedDownEpisodePath =
    D.Utils.Route.isBackstagePodcastThumbedDownEpisodesPath(currentPath);
  const shouldAddToList =
    (isThumbedUpEpisodePath && isAddingThumbUp) || (isThumbedDownEpisodePath && isAddingThumbDown);
  const shouldRemoveFromList =
    (isThumbedUpEpisodePath && (isRemovingThumbUp || isAddingThumbDown)) ||
    (isThumbedDownEpisodePath && (isRemovingThumbDown || isAddingThumbUp));

  if (shouldAddToList) {
    yield put(PodcastActions.addEpisodeToThumbsList(episodeId));
  } else if (shouldRemoveFromList) {
    yield put(PodcastActions.removeEpisodeFromThumbsList(episodeId));
  }
}

export function* onItemFeedbackAdded(action) {
  yield call(openFeedbackModal, action);

  // derive ids and validate
  const { programId, episodeId } = yield call(getItemFeedbackIds, action);
  const activeProgramId = yield select(PodcastUtils.getActiveBackstageProgramId);

  if (programId && programId === activeProgramId) {
    // refresh thumb count
    yield put(PodcastActions.requestGetDetails({ pandoraId: programId }));
    // update thumbs list
    yield call(updateThumbsList, {
      episodeId,
      isAddingThumbUp: action.payload.isPositive,
      isAddingThumbDown: !action.payload.isPositive,
    });
  }
}

export function* onItemFeedbackRemoved(action) {
  // derive ids and validate
  const { programId, episodeId } = yield call(getItemFeedbackIds, action);
  const activeProgramId = yield select(PodcastUtils.getActiveBackstageProgramId);

  if (programId && programId === activeProgramId) {
    // refresh thumb count
    yield put(PodcastActions.requestGetDetails({ pandoraId: programId }));
    // update thumbs list
    yield call(updateThumbsList, {
      episodeId,
      isRemovingThumbUp: action.payload.isPositive,
      isRemovingThumbDown: !action.payload.isPositive,
    });
  }
}

export function* onAesopGetDetailsSuccess(action) {
  const pandoraId = get(action, 'params.content.pandoraId');
  pandoraId && (yield handleRemovedContent({ pandoraId }));
}

export function* handleRemovedContent({ pandoraId }) {
  const annotation = yield select(D.getAnnotationsById, pandoraId);

  if (D.isRemoved(annotation)) {
    yield put(push(D.getCollectionPath()));
    yield put(D.Actions.Modal.openModalCoachmark(PodcastConstants.messages.podcastUnavailable));
  }
}

export function* openFeedbackModal({ payload: { sourceId, isPositive } }) {
  const sourceType = D.Utils.SourceType.getSourceTypeFromPandoraId(sourceId);
  if (!D.Constants.SourceTypes.PodcastSourceTypes.includes(sourceType)) {
    return;
  }
  if (isPositive) {
    const podcastFeedbackCount = yield select(
      D.getInDomain('listener.selectors.getPodcastFeedbackCount'),
      0,
    );
    if (podcastFeedbackCount < D.Constants.ListenerExternals.MAX_PODCAST_POSITIVE_FEEDBACK_COUNT) {
      const modalConfig = {
        id: D.Constants.MiniCoachmark.MiniCoachmarkTypes.THUMB_UP,
        modalType: 'ModalCoachMark',
        body: <FormattedMessage {...PodcastConstants.messages.podcastThumbUpInformation} />,
      };
      yield put(D.Actions.Modal.openModal(modalConfig));
    }
    return;
  }

  const { dispatch } = D.getStore();
  const thumbDownType = D.Constants.MiniCoachmark.MiniCoachmarkTypes.THUMB_DOWN;
  const modalConfig = {
    modalType: 'ModalCoachMark',
    id: thumbDownType,
    styleNames: ['withLargeButton'],
    body: <FormattedMessage {...PodcastConstants.messages.podcastThumbDownInformation} />,
    buttons: [
      <D.ButtonLink
        key={0}
        to={D.Utils.Route.getBrowsePodcastsPath()}
        handleClick={() => {
          dispatch(D.Actions.Modal.closeModal());
          dispatch(
            D.Actions.MiniCoachmark.coachmarkClicked(
              thumbDownType,
              D.Constants.Stats.CoachmarkReasons.CLICK,
            ),
          );
        }}
        title={PodcastConstants.messages.browseMorePodcasts}
        styleNames={[D.ButtonLinkStyles.CTA_T3]}
      />,
    ],
  };
  yield put(D.Actions.Modal.openModal(modalConfig));
}

export function* requestAnnotationsFromAction(action) {
  const annotations = get(action, 'response.annotations') || {};
  const pandoraItems = Object.values(annotations).filter(item => {
    return D.Utils.SourceType.isPodcastPandoraType(item.type);
  });
  let pandoraIds = pandoraItems.map(item => item.pandoraId);
  pandoraIds = yield select(PodcastUtils.getPodcastIdsShouldRefreshMetadata, pandoraIds);
  if (pandoraIds.length) {
    // Since we just loaded these annotations we're only interested in the non-annotation
    // metadata returned by the Aesop annotateObjecst API method
    yield put(
      D.requestAnnotateAesopObjects({
        pandoraIds,
        ignoreAnnotations: true,
      }),
    );
  }
}
